import React, { useContext } from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import MomentUtils from '@date-io/moment';
import Button from '@material-ui/core/Button';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import type { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import type Candidate from '@this/domain/appoint/candidate';
import AppointContext from '@this/components/appoint/appoint_context';
import muiTheme from '@this/components/appoint/mui_theme';
import type OpenMeeting from '@this/domain/appoint/open_meeting';

interface Props {
  openMeeting: OpenMeeting;
  candidateSlots: Candidate[];
  selectedCandidate: Candidate | null | undefined;
  setSelectedCandidate: (candidate: Candidate | null) => void;
  selectedDate: MaterialUiPickersDate;
  setSelectedDate: (date: MaterialUiPickersDate) => void;
  toggleSelectedCandidate: (candidate: Candidate) => void;
}

const OpenMeetingShowCandidateArea = ({
  openMeeting,
  candidateSlots,
  selectedCandidate,
  setSelectedCandidate,
  selectedDate,
  setSelectedDate,
  toggleSelectedCandidate
}: Props) => {
  const contextValue = useContext(AppointContext);

  const shouldDisableDate = (d: MaterialUiPickersDate) => {
    if (d && openMeeting.candidates.length > 0) {
      const selectableDates = openMeeting.candidates.map(c => c.start.format('YYYY-MM-DD'));
      return selectableDates.indexOf(d.format('YYYY-MM-DD')) === -1;
    }
    return true;
  };

  const handleSetSelectedDate = (d: MaterialUiPickersDate) => {
    const newDate = d ? d.format('YYYY-MM-DD') : null;
    const oldDate = selectedDate ? selectedDate.format('YYYY-MM-DD') : null;
    const candidateDate = selectedCandidate ? selectedCandidate.start.format('YYYY-MM-DD') : null;

    if (oldDate && newDate !== oldDate) {
      if (candidateDate && candidateDate !== newDate) {
        setSelectedCandidate(null);
      }
      setSelectedDate(d);
    }
  };

  return (
    <CandidatesArea>
      {candidateSlots.length > 0 && (
        <CandidatesBox>
          {contextValue.isMobile ? (
            <CandidatesSelectorBox>
              {candidateSlots.map(c => (
                <CandidatesMobileButtonBox key={c.start.format()}>
                  <Button
                    variant={
                      selectedCandidate && selectedCandidate.start.isSame(c.start) ? 'contained' : 'outlined'
                    }
                    color="primary"
                    fullWidth
                    onClick={() => toggleSelectedCandidate(c)}
                  >
                    {c.start.format('M月D日 (ddd) HH:mm 〜')}
                  </Button>
                </CandidatesMobileButtonBox>
              ))}
            </CandidatesSelectorBox>
          ) : (
            <>
              <CandidatesCalendarBox>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <DatePicker
                    onChange={(d: MaterialUiPickersDate) => handleSetSelectedDate(d)}
                    shouldDisableDate={(d: MaterialUiPickersDate) => shouldDisableDate(d)}
                    value={selectedDate}
                    disableToolbar
                    fullWidth
                    variant="static"
                    disablePast
                  />
                </MuiPickersUtilsProvider>
              </CandidatesCalendarBox>
              <CandidatesSelectorBox>
                <Typography style={{ width: '100%', padding: '9px 0 6px', fontSize: '12px', lineHeight: '20px' }}>
                  {`
                    ${candidateSlots[0].start.format(
                      candidateSlots[0].start.year() !== moment().year() ? 'YYYY年M月D日 (ddd) ' : 'M月D日 (ddd) '
                    )}
                    ${openMeeting.duration}分
                  `}
                </Typography>
                {candidateSlots.map(c => (
                  <CandidatesButtonBox key={c.start.format()}>
                    <Button
                      variant={
                        selectedCandidate && selectedCandidate.start.isSame(c.start) ? 'contained' : 'outlined'
                      }
                      color="primary"
                      fullWidth
                      onClick={() => toggleSelectedCandidate(c)}
                    >
                      {c.start.format('HH:mm 〜')}
                    </Button>
                  </CandidatesButtonBox>
                ))}
              </CandidatesSelectorBox>
            </>
          )}
        </CandidatesBox>
      )}
    </CandidatesArea>
  );
};

const CandidatesArea = styled.div``;

const CandidatesBox = styled.div``;

const CandidatesMobileButtonBox = styled.div`
  width: 100%;
  padding: 4px 0;
`;

const CandidatesCalendarBox = styled.div`
  margin-right: 44px;
  padding: 12px 4px 0;
  .MuiPickersStaticWrapper-staticWrapperRoot {
    min-width: auto;
  }

  .MuiPickersCalendarHeader-switchHeader {
    margin: 4px 0;

    .MuiIconButton-root {
      padding: 4px;

      svg {
        font-size: 20px;
      }
    }

    .MuiPickersCalendarHeader-transitionContainer {
      order: -1;

      .MuiTypography-alignCenter {
        padding-left: 10px;
        font-size: 12px;
        text-align: left;
      }
    }
  }
  .MuiPickersBasePicker-pickerView {
    max-width: none;
    min-width: 196px;
    min-height: 204px;
    height: 204px;

    .MuiPickersCalendar-transitionContainer {
      min-height: 140px;
      height: 140px;
    }
  }
  .MuiPickersCalendarHeader-dayLabel {
    width: 28px;
    margin: 0;
    color: inherit;
    font-size: 10px;
  }
  .MuiPickersSlideTransition-transitionContainer > * {
    position: relative;
  }
  .MuiPickersDay-day {
    width: 28px;
    height: 28px;
    margin: 0;
    background: ${muiTheme.palette.primary.light};
    font-size: 12px;
    &.MuiPickersDay-current {
      color: black;
      background: #fff;
      border: solid 1px ${muiTheme.palette.primary.main};
    }
    &.MuiPickersDay-daySelected {
      background: ${muiTheme.palette.primary.main};
    }
    &.MuiPickersDay-dayDisabled {
      color: black;
      background: #fff;
    }

    .MuiTypography-body2 {
      font-size: 10px;
    }
  }
`;

const CandidatesSelectorBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  place-content: flex-start;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  padding: 12px;
`;

const CandidatesButtonBox = styled.div`
  box-sizing: border-box;
  width: 50%;
  padding: 2px;
`;

export default OpenMeetingShowCandidateArea;
